import { i18n } from "@/main";
import { AnalysisCreditsSchema, OrderProductPackageSchema, OrderType, PaymentStatus, PaymentType } from "../volateq-api/api-schemas/order-schema";
import { ApiProductPackage } from "../volateq-api/api-product-packages";
import type { AnalysisForViewSchema } from "../volateq-api/api-schemas/analysis-schema";
import type { MultiselectOption } from "@/app/shared/components/app-multiselect/types";
import { MathHelper } from "./math-helper";

export interface OrderExceedContext {
  plantCapacityMw?: number | null;
  /** Package is already saved on this analysis (use per-analysis progress). */
  alreadyLinked?: boolean;
  /** Creating a new analysis (preview includes this plant / +1 count). */
  isNewAnalysis?: boolean;
  /** The Soiling package is selected, so a new analysis costs 1.5 times the credits. */
  hasSoiling?: boolean;
}

/** Order product packages shown as one pill: a Wallet's packages share its credits, so they are grouped. */
export interface OrderPPDisplayGroup {
  key: string;
  /** Carries the progress of the group (the same for every package of a Wallet). */
  orderPP: OrderProductPackageSchema;
  name: string;
  packageCount: number;
  disabledKeyFiguresCount: number;
}

export interface OrderPPMultiselectOption extends MultiselectOption {
  productPackageName: string;
  /** Progress shown behind the name, e.g. "2/10 Credits". */
  usage?: string;
  /** Set for Wallet packages, whose selected tags are grouped per Wallet. */
  walletOrderId?: string;
}

export interface OrderPPSelectionGroup {
  key: string;
  options: OrderPPMultiselectOption[];
  usage?: string;
}

export class OrderHelper {
  static readonly ORDER_TYPES = [OrderType.SETUP, OrderType.SUBSCRIPTION, OrderType.MW_SUBSCRIPTION, OrderType.WALLET];
  /** Booked for all plants of a customer with one technology instead of a single plant. */
  static readonly CUSTOMER_WIDE_ORDER_TYPES = [OrderType.MW_SUBSCRIPTION, OrderType.WALLET];

  static readonly WALLET_DEFAULT_CREDITS = 25;
  static readonly WALLET_DEFAULT_PRODUCT_PACKAGE_IDS = [
    ApiProductPackage.PV_THERMOGRAPHY_SURVEY_FULL_HD,
    ApiProductPackage.PV_THERMOGRAPHY_IEC62446,
    ApiProductPackage.PV_SOILING,
  ];
  static readonly PV_THERMOGRAPHY_SURVEY_IDS = [
    ApiProductPackage.PV_THERMOGRAPHY_SURVEY_FULL_HD,
    ApiProductPackage.PV_THERMOGRAPHY_SURVEY_4K,
  ];
  /** Same factor as the backend (WalletCreditService). */
  static readonly SOILING_CREDIT_FACTOR = 1.5;
  static readonly PAYMENT_TYPES = [PaymentType.PRE_PAID, PaymentType.INVOICED];
  static readonly PAYMENT_STATUSES = [PaymentStatus.UNPAID, PaymentStatus.PAID];

  static readonly ORDER_TYPE_TRANSLATION_KEYS: Record<OrderType, string> = {
    [OrderType.SETUP]: "setup",
    [OrderType.SUBSCRIPTION]: "subscription",
    [OrderType.MW_SUBSCRIPTION]: "mw-subscription",
    [OrderType.WALLET]: "wallet",
  };

  static readonly ORDER_TYPE_DESCRIPTION_TRANSLATION_KEYS: Record<OrderType, string> = {
    [OrderType.SETUP]: "setup_expl",
    [OrderType.SUBSCRIPTION]: "subscription_expl",
    [OrderType.MW_SUBSCRIPTION]: "mw-subscription_expl",
    [OrderType.WALLET]: "wallet_expl",
  };

  static readonly PAYMENT_TYPE_TRANSLATION_KEYS: Record<PaymentType, string> = {
    [PaymentType.PRE_PAID]: "pre-paid",
    [PaymentType.INVOICED]: "invoiced",
  };

  static readonly PAYMENT_STATUS_TRANSLATION_KEYS: Record<PaymentStatus, string> = {
    [PaymentStatus.UNPAID]: "unpaid",
    [PaymentStatus.PAID]: "paid",
  };

  /** MW subscriptions and wallets are paid up front, all other orders are invoiced. */
  static defaultPaymentType(orderType: OrderType | null): PaymentType {
    return orderType === OrderType.MW_SUBSCRIPTION || orderType === OrderType.WALLET
      ? PaymentType.PRE_PAID
      : PaymentType.INVOICED;
  }

  static isCustomerWide(orderType: OrderType | null): boolean {
    return orderType !== null && this.CUSTOMER_WIDE_ORDER_TYPES.includes(orderType);
  }

  /** The capacity rounded to whole MW (half up), at least 1. Same rule as the backend (WalletCreditService). */
  static calcMwCredits(capacityMw?: number | null): number {
    return capacityMw ? Math.max(1, Math.floor(MathHelper.roundToNumber(capacityMw, 3) + 0.5)) : 1;
  }

  /** Credits of an analysis: 1 per MW, times 1.5 with Soiling, not rounded afterwards (same rule as the backend). */
  static calcCredits(capacityMw: number | null | undefined, hasSoiling: boolean): number {
    const mwCredits = this.calcMwCredits(capacityMw);
    return hasSoiling ? mwCredits * this.SOILING_CREDIT_FACTOR : mwCredits;
  }

  /** What an analysis with the selected packages takes from its Wallet, null without a Wallet package.
   * An admin's credit_usage_override wins, as for the backend. */
  static selectedAnalysisCredits(
    selectedOrderPPs: OrderProductPackageSchema[],
    capacityMw: number | null | undefined,
    creditUsageOverride?: number | null,
  ): number | null {
    if (!selectedOrderPPs.some(orderPP => orderPP.booked_credits != null)) {
      return null;
    }
    if (creditUsageOverride != null) {
      return creditUsageOverride;
    }
    return this.calcCredits(capacityMw, this.walletHasSoiling(selectedOrderPPs));
  }

  /** Groups items of the same Wallet (keeping the order of their first appearance), every other item stays alone. */
  static groupByWallet<T>(items: T[], walletOrderIdOf: (item: T) => string | undefined): Array<{ key: string; items: T[] }> {
    const groups: Array<{ key: string; items: T[] }> = [];
    const walletGroups = new Map<string, { key: string; items: T[] }>();
    items.forEach((item, index) => {
      const walletOrderId = walletOrderIdOf(item);
      const walletGroup = walletOrderId !== undefined ? walletGroups.get(walletOrderId) : undefined;
      if (walletGroup) {
        walletGroup.items.push(item);
        return;
      }
      const group = { key: walletOrderId !== undefined ? `wallet-${walletOrderId}` : `item-${index}`, items: [item] };
      if (walletOrderId !== undefined) {
        walletGroups.set(walletOrderId, group);
      }
      groups.push(group);
    });
    return groups;
  }

  static walletOrderIdOf(orderPP: OrderProductPackageSchema): string | undefined {
    return orderPP.booked_credits != null ? orderPP.order_id : undefined;
  }

  static groupForDisplay(orderPPs: OrderProductPackageSchema[]): OrderPPDisplayGroup[] {
    return this.groupByWallet(orderPPs, orderPP => this.walletOrderIdOf(orderPP)).map(group => ({
      key: group.key,
      orderPP: group.items[0],
      name: group.items.map(orderPP => orderPP.product_package.name).join(", "),
      packageCount: group.items.length,
      disabledKeyFiguresCount: group.items.reduce((sum, orderPP) => sum + (orderPP.disabled_key_figures_count || 0), 0),
    }));
  }

  static groupSelectedOptions(options: OrderPPMultiselectOption[]): OrderPPSelectionGroup[] {
    return this.groupByWallet(options, option => option.walletOrderId).map(group => ({
      key: group.key,
      options: group.items,
      usage: group.items[0].usage,
    }));
  }

  /** "Name (usage)" per package, but one entry per Wallet: "Soiling, Thermography Survey (2/10 Credits)". */
  static groupedUsageLabels(
    orderPPs: OrderProductPackageSchema[],
    contextOf: (orderPP: OrderProductPackageSchema) => OrderExceedContext,
  ): string {
    return this.groupForDisplay(orderPPs)
      .map(group => `${group.name} (${this.usageLabel(group.orderPP, contextOf(group.orderPP))})`)
      .join(", ");
  }

  static selectedOrderPPs(availableOrderPPs: OrderProductPackageSchema[], selectedIds: string[] | null): OrderProductPackageSchema[] {
    const selected = new Set(selectedIds ?? []);
    return availableOrderPPs.filter(orderPP => selected.has(orderPP.id));
  }

  /** IEC and a Thermography Survey cover the same, so an analysis should only have one of them. */
  static hasIecAndSurvey(productPackageIds: number[]): boolean {
    return productPackageIds.includes(ApiProductPackage.PV_THERMOGRAPHY_IEC62446)
      && productPackageIds.some(productPackageId => this.PV_THERMOGRAPHY_SURVEY_IDS.includes(productPackageId));
  }

  /** The two thermal products: a Thermography Survey (Full HD or 4K) or IEC 62446. */
  static hasThermography(analysis: AnalysisForViewSchema): boolean {
    const productPackageIds = [
      ...analysis.order_product_packages.map(orderPP => orderPP.product_package.id),
      ...(analysis.demo_product_packages || []).map(productPackage => productPackage.id),
    ];

    return productPackageIds.some(productPackageId =>
      productPackageId === ApiProductPackage.PV_THERMOGRAPHY_IEC62446 || this.PV_THERMOGRAPHY_SURVEY_IDS.includes(productPackageId));
  }

  /** Same rule as the backend: a Wallet takes the credits for the whole analysis, so it cannot be combined with other orders. */
  static mixesWalletWithOtherOrders(orderPPs: OrderProductPackageSchema[]): boolean {
    return orderPPs.some(orderPP => orderPP.booked_credits != null)
      && orderPPs.some(orderPP => orderPP.booked_credits == null);
  }

  /** Same assumption as the backend proposal: a new analysis uses the Soiling package of its Wallet. */
  static walletHasSoiling(orderPPs: OrderProductPackageSchema[]): boolean {
    return orderPPs.some(orderPP => orderPP.booked_credits != null && orderPP.product_package.id === ApiProductPackage.PV_SOILING);
  }

  /** Credits can be half (Soiling), so they are formatted with the decimal separator of the locale. */
  static formatCreditCount(credits: number): string {
    return credits.toLocaleString(i18n.locale, { maximumFractionDigits: 1 });
  }

  /** e.g. "2.5 Credits" */
  static formatCreditAmount(credits: number): string {
    return `${this.formatCreditCount(credits)} ${i18n.t("credits")}`;
  }

  static formatCredits(used: number | null, booked: number): string {
    return `${used == null ? "?" : this.formatCreditCount(used)}/${this.formatCreditCount(booked)} ${i18n.t("credits")}`;
  }

  /** Explains in a few lines why an analysis takes the credits it does. */
  static creditsExplanationLines(analysisCredits: AnalysisCreditsSchema): string[] {
    const lines = [i18n.t("analysis-credits_expl", { credits: this.formatCreditCount(analysisCredits.credits) }).toString()];

    if (analysisCredits.credit_usage_override != null) {
      lines.push(i18n.t("analysis-credits_expl-override", {
        calculated: this.formatCreditCount(analysisCredits.calculated_credits),
      }).toString());
      return lines;
    }

    lines.push(i18n.t("analysis-credits_expl-mw", {
      mw: analysisCredits.capacity_mw != null ? MathHelper.formatMw(analysisCredits.capacity_mw) : "?",
      credits: analysisCredits.mw_credits,
    }).toString());
    if (analysisCredits.has_soiling) {
      lines.push(i18n.t("analysis-credits_expl-soiling", {
        credits: this.formatCreditCount(analysisCredits.calculated_credits),
      }).toString());
    }

    return lines;
  }

  /** An analysis needs no invoice of its own if every order it is booked on is paid (same rule as the backend). */
  static isPaidByOrders(paymentStatuses: PaymentStatus[]): boolean {
    return paymentStatuses.length > 0 && paymentStatuses.every(paymentStatus => paymentStatus === PaymentStatus.PAID);
  }

  /** Current consumed MW plus this plant, when creating a new analysis. */
  static previewAnalysesMw(
    orderPP: OrderProductPackageSchema,
    plantCapacityMw?: number | null,
  ): number {
    const totalMw = orderPP.total_analyses_mw || 0;
    return plantCapacityMw ? totalMw + plantCapacityMw : totalMw;
  }

  /** Next analysis slot on a quantity subscription (same preview as the multiselect label). */
  static previewAnalysesCount(orderPP: OrderProductPackageSchema): number {
    return orderPP.total_analyses_count + 1;
  }

  static usedMw(
    orderPP: OrderProductPackageSchema,
    context: OrderExceedContext = {},
  ): number | null {
    if (orderPP.booked_mw == null) {
      return null;
    }
    if (context.alreadyLinked) {
      return orderPP.analysis_mw_within_order_product_package ?? null;
    }
    if (context.isNewAnalysis) {
      return this.previewAnalysesMw(orderPP, context.plantCapacityMw);
    }
    return orderPP.total_analyses_mw || 0;
  }

  static usedCount(
    orderPP: OrderProductPackageSchema,
    context: OrderExceedContext = {},
  ): number | null {
    if (!orderPP.booked_quantity) {
      return null;
    }
    if (context.alreadyLinked) {
      return orderPP.analysis_position_within_order_product_package ?? null;
    }
    return this.previewAnalysesCount(orderPP);
  }

  static usedCredits(
    orderPP: OrderProductPackageSchema,
    context: OrderExceedContext = {},
  ): number | null {
    if (orderPP.booked_credits == null) {
      return null;
    }
    if (context.alreadyLinked) {
      return orderPP.analysis_credits_within_order_product_package ?? null;
    }
    const totalCredits = orderPP.total_analyses_credits || 0;
    if (context.isNewAnalysis) {
      return totalCredits + this.calcCredits(context.plantCapacityMw, !!context.hasSoiling);
    }
    return totalCredits;
  }

  static wouldExceedWallet(
    orderPP: OrderProductPackageSchema,
    context: OrderExceedContext = {},
  ): boolean {
    const used = this.usedCredits(orderPP, context);
    return used != null && orderPP.booked_credits != null && used > orderPP.booked_credits;
  }

  static wouldExceedMwSubscription(
    orderPP: OrderProductPackageSchema,
    context: OrderExceedContext = {},
  ): boolean {
    const used = this.usedMw(orderPP, context);
    if (used == null || orderPP.booked_mw == null) {
      return false;
    }
    return MathHelper.roundToNumber(used, 3) > MathHelper.roundToNumber(orderPP.booked_mw, 3);
  }

  static wouldExceedQuantitySubscription(
    orderPP: OrderProductPackageSchema,
    context: OrderExceedContext = {},
  ): boolean {
    const used = this.usedCount(orderPP, context);
    if (used == null || !orderPP.booked_quantity) {
      return false;
    }
    return used > orderPP.booked_quantity;
  }

  static wouldExceedOrder(
    orderPP: OrderProductPackageSchema,
    context: OrderExceedContext = {},
  ): boolean {
    return this.wouldExceedMwSubscription(orderPP, context)
      || this.wouldExceedWallet(orderPP, context)
      || this.wouldExceedQuantitySubscription(orderPP, context);
  }

  static usageLabel(
    orderPP: OrderProductPackageSchema,
    context: OrderExceedContext = {},
  ): string {
    if (orderPP.booked_mw != null) {
      const used = this.usedMw(orderPP, context);
      const usedLabel = used == null ? "?" : MathHelper.formatMw(used);
      return `${usedLabel}/${MathHelper.formatMw(orderPP.booked_mw)} MW`;
    }
    if (orderPP.booked_credits != null) {
      return this.formatCredits(this.usedCredits(orderPP, context), orderPP.booked_credits);
    }
    const used = this.usedCount(orderPP, context);
    const usedLabel = used == null ? "?" : String(used);
    return `${usedLabel}/${orderPP.booked_quantity}`;
  }
}
